import type { ReportEntriesPagination, VatJson, VatReportEntry, VatSummaryLine } from '@query'
import React, { useEffect, useMemo, useState } from 'react'
import { useInteractiveReport } from '@query'
import type { ReportRequestBody } from '@query'
import { VatLineEntries } from './VatLineEntries'
import { ENTRY_PANEL_PAGE_SIZE } from './entryPanelConstants'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  lineId: string
  currency?: string
}

const toVatScopedEntries = (line: VatSummaryLine) => ({
  entries: line.entries,
  entries_count: line.entries_count,
  entries_pagination: line.entries_pagination
})

const findVatScopedEntries = (
  data: VatJson | undefined,
  lineId: string
): {
  entries: VatReportEntry[]
  entries_count?: number
  entries_pagination?: ReportEntriesPagination
} | null => {
  if (!data) return null

  const [root, suffix] = lineId.includes(':') ? lineId.split(':', 2) : [lineId, null]

  if (root === 'map' && suffix != null) {
    const mappingLine = data.mapping?.[suffix]
    if (!mappingLine || typeof mappingLine !== 'object' || !('entries' in mappingLine)) {
      return null
    }
    return toVatScopedEntries(mappingLine as VatSummaryLine)
  }

  const summary = data.summary
  if (!summary) return null

  if (root === 'domestic_sales' && suffix != null) {
    const domestic = summary.domestic_sales as Record<string, VatSummaryLine> | undefined
    const line = domestic?.[suffix]
    if (!line) return null
    return toVatScopedEntries(line)
  }

  const line = summary[root as keyof typeof summary]
  if (!line || typeof line !== 'object' || !('entries' in line)) {
    return null
  }
  return toVatScopedEntries(line as VatSummaryLine)
}

export const VatLineEntriesPanel: React.FC<Props> = ({ slug, reportBody, lineId, currency }) => {
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [lineId])

  const body = useMemo(
    () => ({
      ...reportBody,
      vat_entry_group: lineId,
      entries_page: page,
      entries_page_size: ENTRY_PANEL_PAGE_SIZE
    }),
    [reportBody, lineId, page]
  )

  const { data, isFetching, isLoading } = useInteractiveReport<VatJson>({
    slug,
    reportKey: 'vat',
    body
  })

  const line = findVatScopedEntries(data, lineId)
  const showLoading = isLoading || isFetching
  const pagination = line?.entries_pagination

  return (
    <VatLineEntries
      lineId={lineId}
      entries={line?.entries ?? []}
      currency={currency}
      isLoading={showLoading}
      entriesPage={page}
      entriesPageCount={pagination?.page_count ?? 1}
      entriesTotalCount={pagination?.total_count ?? line?.entries_count ?? 0}
      entriesPageSize={pagination?.page_size}
      onEntriesPageChange={setPage}
    />
  )
}
